import type { FC } from "react";
import { AlertCircle, PauseCircle, XCircle } from "@untitledui/icons";
import { IconOnly, statuses } from "../components/application/progress-steps/progress-step-base";
import { type DateLike, daysUntil, formatJalali, toDate } from "../lib/jalali";
import { REASONS, type ReasonCode } from "../lib/reasons";
import { cx } from "../utils/cx";

export type CommissionBranchKind = "held" | "reversed" | "void";

export interface CommissionTimelineData {
    /** Order placed in WHMCS. */
    orderedAt: DateLike;
    /** Invoice paid; commission created as pending. */
    paidAt?: DateLike;
    /** End of the guarantee period (`eligible_at`). */
    eligibleAt?: DateLike;
    /** Sweep approved it. */
    approvedAt?: DateLike;
    /** Added to the withdrawable balance (usually equals approvedAt). */
    availableAt?: DateLike;
    /** Paid out to the partner. */
    paidOutAt?: DateLike;
    payoutChannel?: "wallet" | "bank";
    /** Off-path outcome: held for review, reversed by refund, or void. */
    branch?: { kind: CommissionBranchKind; at: DateLike; reason: ReasonCode };
}

type StepStatus = "complete" | "current" | "incomplete";

type Row = {
    key: string;
    title: string;
    status: StepStatus;
    date?: string;
    /** Expected date for future steps, shown muted. */
    expected?: string;
    note: string;
};

const branchCopy: Record<CommissionBranchKind, { title: string; icon: FC<{ className?: string }>; ring: string; fg: string; text: string }> = {
    held: { title: "برای بررسی نگه داشته شد", icon: PauseCircle, ring: "bg-warning-primary ring-utility-amber-200", fg: "text-fg-warning-primary", text: "text-warning-primary" },
    reversed: { title: "برگشت خورد", icon: XCircle, ring: "bg-error-primary ring-error_subtle", fg: "text-fg-error-primary", text: "text-error-primary" },
    void: { title: "باطل شد", icon: AlertCircle, ring: "bg-secondary ring-primary", fg: "text-fg-quaternary", text: "text-secondary" },
};

function buildRows(data: CommissionTimelineData, now: Date): Row[] {
    const { orderedAt, paidAt, eligibleAt, approvedAt, availableAt, paidOutAt, payoutChannel = "wallet" } = data;
    const guaranteeOver = eligibleAt ? daysUntil(eligibleAt, now) <= 0 : false;
    const available = availableAt ?? approvedAt;
    const daysLeft = eligibleAt ? daysUntil(eligibleAt, now) : 0;
    const guaranteeTitle =
        paidAt && !approvedAt && !guaranteeOver && eligibleAt ? `در دورهٔ ضمانت (${daysLeft} روز مانده)` : "دورهٔ ضمانت";

    const rows: Row[] = [
        {
            key: "ordered",
            title: "سفارش ثبت شد",
            status: "complete",
            date: formatJalali(orderedAt),
            note: "مشتری با کد یا لینک شما سفارش داد و سفارش به نام شما ثبت شد.",
        },
        {
            key: "paid",
            title: "پرداخت شد",
            status: paidAt ? "complete" : "current",
            date: paidAt ? formatJalali(paidAt) : undefined,
            note: paidAt ? "مشتری فاکتور را پرداخت کرد و پورسانت از همین لحظه محاسبه شد." : "منتظر پرداخت فاکتور از طرف مشتری است.",
        },
        {
            key: "guarantee",
            title: guaranteeTitle,
            status: !paidAt ? "incomplete" : approvedAt || guaranteeOver ? "complete" : "current",
            date: approvedAt || guaranteeOver ? (eligibleAt ? formatJalali(eligibleAt) : undefined) : undefined,
            expected: !approvedAt && !guaranteeOver && eligibleAt ? formatJalali(eligibleAt) : undefined,
            note:
                approvedAt || guaranteeOver
                    ? "دورهٔ ضمانت بدون بازگشت وجه تمام شد."
                    : eligibleAt && paidAt
                      ? "مشتری تا پایان این دوره می‌تواند وجهش را پس بگیرد؛ بعد از آن پورسانت خودکار بررسی و تأیید می‌شود."
                      : "پس از پرداخت، پورسانت ۱۴ روز در انتظار می‌ماند تا اگر مشتری سفارش را لغو کرد، حساب‌ها درست بماند.",
        },
        {
            key: "approved",
            title: "تأیید شد",
            status: approvedAt ? "complete" : guaranteeOver ? "current" : "incomplete",
            date: approvedAt ? formatJalali(approvedAt) : undefined,
            note: approvedAt
                ? "سرویس فعال ماند و پورسانت قطعی شد."
                : guaranteeOver
                  ? "ضمانت تمام شده و پورسانت تا چند دقیقهٔ دیگر خودکار تأیید می‌شود."
                  : "اگر سرویس تا پایان ضمانت فعال بماند، خودکار تأیید می‌شود.",
        },
        {
            key: "available",
            title: "قابل برداشت",
            status: available ? "complete" : "incomplete",
            date: available ? formatJalali(available) : undefined,
            note: available ? "به موجودی قابل برداشت شما اضافه شد." : "پس از تأیید، به موجودی قابل برداشت اضافه می‌شود.",
        },
        {
            key: "paid_out",
            title: "به شما پرداخت شد",
            status: paidOutAt ? "complete" : available ? "current" : "incomplete",
            date: paidOutAt ? formatJalali(paidOutAt) : undefined,
            note: paidOutAt
                ? payoutChannel === "bank"
                    ? "به حساب بانکی (شبای) شما واریز شد."
                    : "به کیف پول میزبان‌فای شما واریز شد."
                : available
                  ? "منتظر درخواست برداشت شما یا تسویهٔ خودکار است."
                  : "پس از قابل برداشت شدن، به کیف پول یا حساب بانکی شما می‌رسد.",
        },
    ];

    return rows;
}

export interface CommissionTimelineProps {
    data: CommissionTimelineData;
    /** Fixed "now" for deterministic rendering. */
    now?: DateLike;
    className?: string;
}

/**
 * The life of one commission as a vertical stepper (spec §14.6.1): ordered → paid → guarantee →
 * approved → available → paid out, each with its Jalali date and a one-line reason for being there.
 * Held, reversed and void outcomes branch off after the last completed step.
 * Built on Untitled UI PRO progress-steps primitives (IconOnly + status styles).
 */
export const CommissionTimeline = ({ data, now, className }: CommissionTimelineProps) => {
    const nowDate = now ? toDate(now) : new Date();
    let rows = buildRows(data, nowDate);
    const branch = data.branch;
    let branchIndex = -1;

    if (branch) {
        const lastComplete = rows.reduce((acc, row, i) => (row.status === "complete" ? i : acc), 0);
        // Everything after the branch point is not going to happen (or is paused while held).
        rows = rows.slice(0, lastComplete + 1);
        branchIndex = rows.length;
    }

    const total = rows.length + (branch ? 1 : 0);

    return (
        <ol className={cx("flex flex-col", className)} aria-label="مراحل پورسانت">
            {rows.map((row, i) => {
                const isLast = i === total - 1;
                const nextStatus: StepStatus | undefined = rows[i + 1]?.status;
                return (
                    <li key={row.key} className="group flex gap-3" aria-current={row.status === "current" ? "step" : undefined}>
                        <div className="flex flex-col items-center self-stretch">
                            <IconOnly status={row.status} size="sm" />
                            {!isLast && (
                                <span
                                    className={cx(
                                        "my-1 flex-1 rounded-xs border-s-2",
                                        row.status === "complete" && nextStatus !== "incomplete" ? statuses.complete.connector : statuses.incomplete.connector,
                                    )}
                                />
                            )}
                        </div>
                        <div className={cx("flex min-w-0 flex-1 flex-col gap-0.5 pt-0.5", !isLast && "pb-5")}>
                            <div className="flex flex-wrap items-baseline justify-between gap-x-3">
                                <p className={cx("text-sm font-semibold", statuses[row.status].title)}>{row.title}</p>
                                {row.date && <span className="text-xs text-tertiary digits-tabular">{row.date}</span>}
                                {!row.date && row.expected && (
                                    <span className="text-xs text-quaternary digits-tabular">پیش‌بینی: {row.expected}</span>
                                )}
                            </div>
                            <p className={cx("text-sm", row.status === "incomplete" ? "text-quaternary" : "text-tertiary")}>{row.note}</p>
                        </div>
                    </li>
                );
            })}

            {branch && branchIndex >= 0 && (
                <BranchRow kind={branch.kind} at={branch.at} reason={branch.reason} />
            )}
        </ol>
    );
};

const BranchRow = ({ kind, at, reason }: { kind: CommissionBranchKind; at: DateLike; reason: ReasonCode }) => {
    const copy = branchCopy[kind];
    const r = REASONS[reason];
    const Icon = copy.icon;
    return (
        <li className="flex gap-3" aria-current="step">
            <div className="flex flex-col items-center">
                <span className={cx("z-10 flex size-6 items-center justify-center rounded-full ring-1 ring-inset", copy.ring)}>
                    <Icon className={cx("size-4", copy.fg)} aria-hidden="true" />
                </span>
            </div>
            <div className="flex min-w-0 flex-1 flex-col gap-1 pt-0.5">
                <div className="flex flex-wrap items-baseline justify-between gap-x-3">
                    <p className={cx("text-sm font-semibold", copy.text)}>{copy.title}</p>
                    <span className="text-xs text-tertiary digits-tabular">{formatJalali(at)}</span>
                </div>
                <p className="text-sm text-secondary">{r.why}</p>
                <p className="text-sm text-tertiary">{r.next}</p>
                {kind === "held" && <p className="text-xs text-quaternary">پس از بررسی، مسیر از همین مرحله ادامه پیدا می‌کند.</p>}
            </div>
        </li>
    );
};
